import { autorun, makeAutoObservable } from "mobx";

import { IntervalAttributes, IntervalFragment } from "@/gql/graphql";
import { formatISOTimeOfDay, parseTimeOfDay } from "@/utils/datetime";
import { apply } from "@/utils/functions";

export default class Interval {
  id?: string;
  startTime?: Date | null | undefined = null;
  endTime?: Date | null | undefined = null;
  _destroy?: boolean;

  errorMessage?: string;

  get isValid(): boolean {
    return !this.errorMessage;
  }

  get interval(): { start: Date; end: Date } | undefined {
    if (this.startTime && this.endTime) {
      return { start: this.startTime, end: this.endTime };
    }
    return undefined;
  }

  constructor() {
    makeAutoObservable(this);
    autorun(() => {
      this.isEndTimeLesser();
    });
  }

  isEndTimeLesser = () => {
    const startTime = this.startTime;
    const endTime = this.endTime;
    if (!startTime || !endTime) {
      this.errorMessage = "must be a valid interval";
      return this.errorMessage;
    }

    const isEndTimeLesser = new Date(endTime) <= new Date(startTime);

    this.errorMessage = isEndTimeLesser
      ? "must be a valid interval"
      : undefined;
  };

  setIntervalStartTime = (date: Date | null) => {
    this.startTime = date;
  };

  setIntervalEndTime = (date: Date | null) => {
    this.endTime = date;
  };

  toAttributes = (): IntervalAttributes => {
    return {
      id: this.id,
      startTime: formatISOTimeOfDay(this.startTime ?? new Date()),
      endTime: formatISOTimeOfDay(this.endTime ?? new Date()),
      _destroy: this._destroy,
    };
  };

  restore = () => {
    this._destroy = false;
  };

  static fromFragment(fragment: IntervalFragment) {
    const newInterval = new Interval();
    newInterval.startTime = apply(fragment.startTime, parseTimeOfDay);
    newInterval.endTime = apply(fragment.endTime, parseTimeOfDay);
    newInterval.id = fragment.id;

    return newInterval;
  }
}
